import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createHabit, updateHabit } from "@/data/requests/habit-requests";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

export function CreateHabitDialog({ children, editHabit, onClose, open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!editHabit;
  const { t } = useTranslation();
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      description: "",
      cadence: "daily",
      reminderTime: "",
      targetPerWeek: 7,
    }
  });

  // Populate form when editing
  useEffect(() => {
    if (editHabit) {
      setValue("name", editHabit.name || "");
      setValue("description", editHabit.description || "");
      setValue("cadence", editHabit.cadence || "daily");
      setValue("reminderTime", editHabit.reminderTime || "");
      setValue("targetPerWeek", editHabit.targetPerWeek || 7);
      if (!isControlled) {
        setInternalOpen(true);
      }
    }
  }, [editHabit, setValue, isControlled]);

  const createMutation = useMutation({
    mutationFn: createHabit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["habits"] });
      toast({
        title: t("habits.delete.success") || t("habits.checkedIn"),
        description:
          t("habits.create.successMessage") ||
          t("habits.checkIn.successMessage"),
      });
      handleClose();
    },
    onError: () => {
      toast({
        title: t("common.error"),
        description:
          t("habits.create.errorMessage") ||
          t("habits.checkIn.errorMessage") ||
          t("habits.delete.error"),
        variant: "destructive",
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateHabit(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["habits"] });
      toast({
        title: t("habits.create.title"),
        description:
          t("habits.create.updateSuccess") ||
          t("habits.checkIn.successMessage"),
      });
      handleClose();
    },
    onError: () => {
      toast({
        title: t("common.error"),
        description:
          t("habits.create.errorMessage") ||
          t("habits.delete.error"),
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data) => {
    if (isEditMode) {
      updateMutation.mutate({ id: editHabit.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleClose = () => {
    if (isControlled && onOpenChange) {
      onOpenChange(false);
    } else {
      setInternalOpen(false);
    }
    reset();
    if (onClose) onClose();
  };

  const handleOpenChange = (newOpen) => {
    if (isControlled && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
    if (!newOpen) {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <Plus className="mr-2 h-4 w-4" /> {t("habits.newHabit")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t("habits.create.title") : t("habits.create.title")}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? t("habits.create.updateDescription", {
                  defaultValue: "Update your habit details.",
                })
              : t("habits.create.description", {
                  defaultValue: "Build a new lasting habit.",
                })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">
              {t("habits.create.nameLabel", { defaultValue: "Habit Name" })}
            </Label>
            <Input 
              id="name" 
              placeholder={t("habits.create.namePlaceholder")}
              {...register("name", { required: true })} 
            />
            {errors.name && (
              <span className="text-xs text-red-500">
                {t("habits.create.nameRequired", {
                  defaultValue: "Name is required",
                })}
              </span>
            )}
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">
              {t("habits.create.descriptionLabel", {
                defaultValue: "Description (Optional)",
              })}
            </Label>
            <Textarea 
              id="description" 
              placeholder={t("habits.create.descriptionPlaceholder")}
              {...register("description")} 
              rows={3}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="cadence">
              {t("habits.create.cadence")}
            </Label>
            <Select 
              defaultValue="daily" 
              onValueChange={(value) => setValue("cadence", value)}
            >
              <SelectTrigger>
              <SelectValue
                placeholder={t("habits.create.cadencePlaceholder", {
                  defaultValue: "Select frequency",
                })}
              />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">
                  {t("habits.card.dailyCadence")}
                </SelectItem>
                <SelectItem value="weekdays">
                  {t("habits.create.cadenceWeekdays", {
                    defaultValue: "Weekdays",
                  })}
                </SelectItem>
                <SelectItem value="weekends">
                  {t("habits.create.cadenceWeekends", {
                    defaultValue: "Weekends",
                  })}
                </SelectItem>
                <SelectItem value="weekly">
                  {t("habits.create.cadenceWeekly", {
                    defaultValue: "Weekly",
                  })}
                </SelectItem>
                <SelectItem value="custom">
                  {t("habits.create.cadenceCustom", {
                    defaultValue: "Custom",
                  })}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="targetPerWeek">
              {t("habits.create.targetPerWeek")}
            </Label>
            <Input 
              id="targetPerWeek" 
              type="number" 
              min="1" 
              max="7" 
              placeholder="7"
              {...register("targetPerWeek", { min: 1, max: 7 })} 
            />
            <p className="text-xs text-muted-foreground">
              {t("habits.create.targetHint", {
                defaultValue: "How many times per week do you want to do this?",
              })}
            </p>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="reminderTime">
              {t("habits.create.reminderTime")}
            </Label>
            <Input 
              id="reminderTime" 
              type="time" 
              {...register("reminderTime")} 
            />
            <p className="text-xs text-muted-foreground">
              {t("habits.create.reminderHint", {
                defaultValue: "Set a daily reminder to help you stay on track",
              })}
            </p>
          </div>
        </form>
        <DialogFooter>
          <Button 
            type="submit" 
            onClick={handleSubmit(onSubmit)} 
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending 
              ? t("common.saving", { defaultValue: "Saving..." })
              : isEditMode 
                ? t("habits.create.buttonUpdate", { defaultValue: "Update Habit" })
                : t("habits.create.button")
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
